import React, { useEffect, useState } from 'react';
import '../polygon-modal/styles.scss';
import { Button, Input } from '@cokitchen/cokitchen-components';
import { CloseOutlineIcon } from 'cokitchen-icons';
import { LatLngLiteral, LatLngTuple } from 'leaflet';
import ReactModal from 'react-modal';
import { isEqual } from 'lodash';
import Map from 'components/map';
import { ShopPolygonType } from 'utils/types';

export type PolygonModalType = 'create' | 'edit';

type FormDataType = {
  name: string;
};
type PolygonModalProps = {
  data?: FormDataType;
  positions?: LatLngLiteral[] | LatLngTuple[];
  type?: PolygonModalType;
  show: boolean;
  setnewPolygon?: (polygons: ShopPolygonType) => void;
  onEdit?: (payload: ShopPolygonType) => void;
  onClose: (refetchData?: boolean) => void;
  polygons?: ShopPolygonType[];
  activePolygon?: ShopPolygonType | null;
};

const PolygonModal = ({
  positions,
  data,
  type = 'create',
  show,
  setnewPolygon,
  onClose,
  polygons = [],
  activePolygon,
  onEdit
}: PolygonModalProps) => {
  const [loading, setLoading] = React.useState(false);
  const [formData, setFormData] = React.useState({} as FormDataType);
  const [initialData, setInitialData] = React.useState({} as FormDataType);
  const [cords, setCords] = useState<LatLngLiteral[] | LatLngTuple[]>([]);
  const [initialCords, setInitialCords] = useState<LatLngLiteral[] | LatLngTuple[]>([]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const getCordinates = (cords: LatLngLiteral[] | LatLngTuple[]) => {
    if (!Array.isArray(cords)) return [cords];

    const coordinates = cords?.map((cord: any) => [cord?.lat, cord?.lng]);
    return coordinates;
  };

  const resetForm = () => {
    setFormData({} as FormDataType);
    setCords([]);
  };

  const getUpdatedFields = (): ShopPolygonType => {
    type Type = FormDataType & {
      polygon: number[][];
    };

    if (!activePolygon) {
      throw new Error('Form data or active polygon is missing');
    }

    const initial: Type = {
      ...initialData,
      polygon: getCordinates(initialCords)
    };

    const updated: Type = {
      ...formData,
      polygon: getCordinates(cords)
    };

    const updatedFields: ShopPolygonType = {
      id: activePolygon.id,
      name: data?.name ?? activePolygon.name,
      polygon: activePolygon.polygon
    };

    (Object.keys(updated) as (keyof Type)[]).forEach((key) => {
      if (!isEqual(updated[key], initial[key])) {
        if (key === 'polygon') {
          updatedFields.polygon = updated.polygon;
        } else if (key === 'name') {
          updatedFields.name = updated.name;
        }
      }
    });

    return updatedFields;
  };

  const updatePolygon = async () => {
    setLoading(true);
    const payload = getUpdatedFields();

    onEdit?.(payload);
  };

  const createShopPolygon = () => {
    if (!formData?.name) return;

    const newCords: any = getCordinates(cords);
    setnewPolygon
      ? setnewPolygon({
          id: String(polygons.length + 1),
          name: formData?.name,
          polygon: newCords
        })
      : null;
    onClose();
    resetForm();
  };

  const handleConfirm = () => {
    if (type === 'create') {
      createShopPolygon();
    } else if (type === 'edit') {
      updatePolygon();
    }
  };

  const btnDisabled = () => {
    return cords?.length === 0 || !formData?.name;
  };
  useEffect(() => {
    if (data && type === 'edit') {
      setFormData(data);
      setCords(positions ?? []);
      setInitialData(data);
      setInitialCords(positions ?? []);
    } else {
      resetForm();
    }
  }, [type, data]);

  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      borderRadius: '10px',
      backgroundColor: '#fff',
      border: 'none',
      padding: '0px',
      overflow: 'unset'
    },
    overlay: {
      background: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(3px)',
      zIndex: 10000
    }
  };

  return (
    <div>
      <ReactModal
        isOpen={show}
        style={customStyles}
        onRequestClose={() => onClose()}
        ariaHideApp={false}>
        <div className='polygon-modal'>
          <div className='polygon-modal__content'>
            <div className='polygon-modal__header'>
              <div></div>
              <h4 className='polygon-modal__title'>
                {type === 'create' ? 'Create' : 'Edit'} Polygon
              </h4>
              <div className='flex justify-center items-center rounded-full bg-[#EFF0F0] w-5 h-5'>
                <CloseOutlineIcon className='cursor-pointer ' onClick={() => onClose()} />
              </div>
            </div>

            <div className='polygon-modal__map px-4'>
              <Map type={type} positions={cords} setCords={setCords} />
            </div>

            <div className='polygon-modal__form'>
              <Input
                name='name'
                value={formData?.name}
                onChange={handleChange}
                label='Polygon Name'
                placeholder='Enter Polygon Name'
                inputClass='h-[40px]'
              />
            </div>

            <div className='polygon-modal__btn'>
              <Button
                onClick={handleConfirm}
                loading={loading}
                color='primary'
                disabled={btnDisabled()}
                variant='block'
                small>
                {type === 'create' ? 'Create' : 'Update'} Polygon
              </Button>
            </div>
          </div>
        </div>
      </ReactModal>
    </div>
  );
};

export default PolygonModal;
